import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import playerService from "../features/player/playerService";
import { GENDERS } from "../utils/profileCompletion";

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  bsa_number: "",
  email: "",
  gender: "",
};

const playerName = (player) =>
  player ? `${player.lastName}, ${player.firstName}` : "";

const pickerLabel = (player) =>
  player ? `${playerName(player)} (${player.bsa_number})` : "";

/**
 * Trims and ignores case, nothing more: a BSA number is an identifier, so
 * leading zeros count. Mirrors the check the server makes.
 */
const bsaMatches = (typed, expected) =>
  String(typed ?? "").trim().toLowerCase() ===
  String(expected ?? "").trim().toLowerCase();

const messageOf = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

/**
 * Search-as-you-type player field for the merge dialog. The picked player is
 * held by the parent; editing the text drops it, so a stale selection can
 * never be merged.
 */
function PlayerPicker({ label, helperText, value, onSelect, disabled }) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // A chosen player settles the field - no more searching until it is cleared.
    if (value) return undefined;

    const term = query.trim();
    if (term.length < 2) {
      setOptions([]);
      setOpen(false);
      setIsSearching(false);
      return undefined;
    }

    let cancelled = false;
    setIsSearching(true);

    const timer = setTimeout(async () => {
      try {
        const response = await playerService.getPlayersWithPagination(0, 10, term);
        if (cancelled) return;
        setOptions(response.data?.players || []);
        setError("");
        setOpen(true);
      } catch (err) {
        if (!cancelled) setError(messageOf(err, "Search failed"));
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, value]);

  const change = (next) => {
    setQuery(next);
    // Typing over a picked player invalidates it.
    if (value) onSelect(null);
    if (next.trim().length < 2) {
      setOptions([]);
      setOpen(false);
    }
  };

  const choose = (player) => {
    onSelect(player);
    setQuery(pickerLabel(player));
    setOptions([]);
    setOpen(false);
  };

  return (
    <Box sx={{ position: "relative" }}>
      <TextField
        fullWidth
        label={label}
        value={value ? pickerLabel(value) : query}
        onChange={(event) => change(event.target.value)}
        disabled={disabled}
        error={Boolean(error)}
        helperText={error || helperText}
        InputProps={{
          endAdornment: isSearching ? <CircularProgress size={16} /> : null,
        }}
      />

      {open && options.length > 0 && (
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            zIndex: 2,
            left: 0,
            right: 0,
            maxHeight: 240,
            overflowY: "auto",
          }}
        >
          <List dense disablePadding>
            {options.map((option) => (
              <ListItemButton
                key={option.id}
                onClick={() => choose(option)}
                disabled={disabled}
              >
                <ListItemText
                  primary={pickerLabel(option)}
                  secondary={option.email || undefined}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}

/**
 * Players: search the whole table, correct a record, delete a duplicate along
 * with every entry it holds, or merge two rows that turned out to be the same
 * person. Duplicates are common - a mistyped BSA number is one digit away from
 * a real one.
 *
 * Uses local state rather than a Redux slice: nothing else in the app reads
 * this data, and it only changes while an admin is on this page.
 *
 * Both destructive actions ask the admin to type the BSA number of the player
 * being deleted. Deleting takes a career's worth of entries with it, and a
 * merge deletes a player outright, so neither should be one mis-click away.
 */
export default function PlayersAdmin() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // Edit
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSummary, setDeleteSummary] = useState(null);
  const [deleteSummaryError, setDeleteSummaryError] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  // Merge
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [mergePreview, setMergePreview] = useState(null);
  const [mergePreviewError, setMergePreviewError] = useState("");
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [mergeConfirmText, setMergeConfirmText] = useState("");
  const [mergeError, setMergeError] = useState("");
  const [isMerging, setIsMerging] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await playerService.getPlayersWithPagination(
        page,
        rowsPerPage,
        debouncedSearch,
      );
      setRows(response.data?.players || []);
      setTotal(response.data?.pagination?.total || 0);
    } catch (err) {
      setError(messageOf(err, "Failed to load players"));
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  // Debounce the search box; a new term always starts back at page 0.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // ---------------------------------------------------------------- editing

  const openEdit = (player) => {
    setEditing(player);
    setForm({
      firstName: player.firstName || "",
      lastName: player.lastName || "",
      bsa_number: player.bsa_number || "",
      email: player.email || "",
      gender: player.gender || "",
    });
    setFormError("");
  };

  const saveEdit = async () => {
    setIsSaving(true);
    setFormError("");

    try {
      await playerService.updatePlayerAdmin(editing.id, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        bsa_number: form.bsa_number.trim(),
        email: form.email.trim() || null,
        gender: form.gender || null,
      });
      setNotice(`Updated ${form.firstName} ${form.lastName}`.trim());
      setEditing(null);
      await load();
    } catch (err) {
      // A BSA number already in use arrives here as a 409.
      setFormError(messageOf(err, "Failed to save player"));
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------------------------------------------------------- deleting

  const openDelete = async (player) => {
    setDeleteTarget(player);
    setDeleteSummary(null);
    setDeleteSummaryError("");
    setDeleteConfirmText("");
    setDeleteError("");

    try {
      const response = await playerService.getPlayerSummary(player.id);
      setDeleteSummary(response.data?.summary || null);
    } catch (err) {
      setDeleteSummaryError(messageOf(err, "Failed to load player"));
    }
  };

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteSummary(null);
    setDeleteConfirmText("");
    setDeleteError("");
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    setDeleteError("");

    try {
      const response = await playerService.deletePlayerAdmin(
        deleteTarget.id,
        deleteConfirmText,
      );
      const deleted = response.data?.deletedEntries ?? 0;
      setNotice(`Deleted ${playerName(deleteTarget)} with ${deleted} entries`);
      closeDelete();

      // The last row on a page leaves the page empty - step back one.
      if (rows.length === 1 && page > 0) {
        setPage(page - 1);
      } else {
        await load();
      }
    } catch (err) {
      setDeleteError(messageOf(err, "Failed to delete player"));
    } finally {
      setIsDeleting(false);
    }
  };

  // ----------------------------------------------------------------- merging

  const openMerge = (sourcePlayer = null) => {
    setMergeOpen(true);
    setMergeSource(sourcePlayer);
    setMergeTarget(null);
    setMergePreview(null);
    setMergePreviewError("");
    setMergeConfirmText("");
    setMergeError("");
  };

  // Any change to the pair invalidates the rehearsal it was based on.
  useEffect(() => {
    setMergePreview(null);
    setMergePreviewError("");
    setMergeConfirmText("");
    setMergeError("");
  }, [mergeSource, mergeTarget]);

  const previewMerge = async () => {
    setIsPreviewing(true);
    setMergePreviewError("");
    setMergeError("");

    try {
      const response = await playerService.mergePlayersPreview(
        mergeSource.id,
        mergeTarget.id,
      );
      setMergePreview(response.data?.preview || null);
    } catch (err) {
      setMergePreview(null);
      setMergePreviewError(messageOf(err, "Failed to preview the merge"));
    } finally {
      setIsPreviewing(false);
    }
  };

  const confirmMerge = async () => {
    setIsMerging(true);
    setMergeError("");

    try {
      const response = await playerService.mergePlayers({
        sourceId: mergeSource.id,
        targetId: mergeTarget.id,
        bsaNumber: mergeConfirmText,
      });
      const moved = response.data?.merge?.movedEntries ?? 0;
      setNotice(
        `Merged ${playerName(mergeSource)} into ${playerName(mergeTarget)} (${moved} entries)`,
      );
      setMergeOpen(false);

      // The source player is gone from the list - step back if it was the
      // only row on this page.
      if (rows.length === 1 && page > 0) {
        setPage(page - 1);
      } else {
        await load();
      }
    } catch (err) {
      setMergeError(messageOf(err, "Failed to merge players"));
    } finally {
      setIsMerging(false);
    }
  };

  const collisions = mergePreview?.collisions || [];
  const mergeBlocked =
    collisions.length > 0 || Boolean(mergePreview?.sourceProfileLinked?.found);
  const mergeReady = Boolean(mergePreview) && !mergeBlocked;
  const mergeConfirmed =
    Boolean(mergeSource) && bsaMatches(mergeConfirmText, mergeSource.bsa_number);

  const deleteConfirmed =
    Boolean(deleteSummary) &&
    bsaMatches(deleteConfirmText, deleteSummary.player.bsa_number);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h4">Players</Typography>
        <Button variant="outlined" onClick={() => openMerge()}>
          Merge players
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every player in the ranking. Deleting removes the player and every entry
        they hold; merging moves one player&apos;s entries onto another and
        deletes the player left empty - the fix for a duplicate created by a
        mistyped BSA number.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <TextField
        fullWidth
        size="small"
        label="Search by name or BSA number"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{ mb: 2, maxWidth: 420 }}
      />

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
          {debouncedSearch
            ? `No players match "${debouncedSearch}".`
            : "No players found."}
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>BSA number</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((player) => (
                <TableRow key={player.id} hover>
                  <TableCell>{playerName(player)}</TableCell>
                  <TableCell>{player.bsa_number}</TableCell>
                  <TableCell>{player.email || "-"}</TableCell>
                  <TableCell>{player.gender || "-"}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openEdit(player)}>
                      Edit
                    </Button>
                    <Button size="small" onClick={() => openMerge(player)}>
                      Merge
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => openDelete(player)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(event, next) => setPage(next)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Edit */}
      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit player</DialogTitle>
        <DialogContent dividers>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}

          <TextField
            autoFocus
            fullWidth
            label="First name"
            value={form.firstName}
            onChange={(event) =>
              setForm({ ...form, firstName: event.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Last name"
            value={form.lastName}
            onChange={(event) =>
              setForm({ ...form, lastName: event.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="BSA number"
            value={form.bsa_number}
            onChange={(event) =>
              setForm({ ...form, bsa_number: event.target.value })
            }
            helperText="Must be unique - the member account links on this number."
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Email"
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            select
            label="Gender"
            value={form.gender}
            onChange={(event) => setForm({ ...form, gender: event.target.value })}
          >
            <MenuItem value="">Unknown</MenuItem>
            {GENDERS.map((gender) => (
              <MenuItem key={gender} value={gender}>
                {gender}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveEdit}
            disabled={isSaving || !form.firstName.trim() || !form.lastName.trim()}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete */}
      <Dialog
        open={deleteTarget !== null}
        onClose={closeDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete player</DialogTitle>
        <DialogContent dividers>
          {deleteSummaryError ? (
            <Alert severity="error">{deleteSummaryError}</Alert>
          ) : !deleteSummary ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Delete {playerName(deleteSummary.player)} (
                {deleteSummary.player.bsa_number})? This permanently deletes{" "}
                {deleteSummary.entryCount === 1
                  ? "1 entry"
                  : `${deleteSummary.entryCount} entries`}{" "}
                along with the player. This cannot be undone.
              </Typography>

              {deleteSummary.profileLinked?.found && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {deleteSummary.player.bsa_number} is the BSA number on a
                  member account
                  {deleteSummary.profileLinked.email
                    ? ` (${deleteSummary.profileLinked.email})`
                    : ""}
                  . The player will be recreated the next time they log in.
                </Alert>
              )}

              {deleteError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {deleteError}
                </Alert>
              )}

              <TextField
                autoFocus
                fullWidth
                label="Type the player's BSA number to confirm"
                value={deleteConfirmText}
                onChange={(event) => setDeleteConfirmText(event.target.value)}
                error={Boolean(deleteConfirmText) && !deleteConfirmed}
                helperText={
                  deleteConfirmText && !deleteConfirmed
                    ? "That does not match this player's BSA number."
                    : " "
                }
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={isDeleting || !deleteSummary || !deleteConfirmed}
          >
            {isDeleting ? "Deleting..." : "Delete player and entries"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge */}
      <Dialog
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Merge players</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Every entry on the source player moves to the target, and the source
            player is deleted. Pick the duplicate as the source and the record
            you want to keep as the target.
          </Typography>

          {mergeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {mergeError}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
            <PlayerPicker
              label="Source - merged from, then deleted"
              value={mergeSource}
              onSelect={setMergeSource}
              disabled={isMerging}
            />
            <PlayerPicker
              label="Target - keeps the entries"
              value={mergeTarget}
              onSelect={setMergeTarget}
              disabled={isMerging}
            />
          </Box>

          <Button
            variant="outlined"
            onClick={previewMerge}
            disabled={
              isPreviewing ||
              isMerging ||
              !mergeSource ||
              !mergeTarget ||
              String(mergeSource.id) === String(mergeTarget.id)
            }
          >
            {isPreviewing ? "Checking..." : "Preview merge"}
          </Button>

          {mergeSource && mergeTarget &&
            String(mergeSource.id) === String(mergeTarget.id) && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                Source and target are the same player.
              </Typography>
            )}

          {mergePreviewError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {mergePreviewError}
            </Alert>
          )}

          {mergePreview && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                Source holds {mergePreview.sourceEntryCount}{" "}
                {mergePreview.sourceEntryCount === 1 ? "entry" : "entries"} →{" "}
                target holds {mergePreview.targetEntryCount}.
              </Typography>

              {mergePreview.sourceProfileLinked?.found && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Blocked: {mergePreview.source.bsa_number} is the BSA number on
                  a member account. Deleting the player would just see it
                  recreated on their next login - merge the other way round, or
                  remove the duplicate's entries by hand first.
                </Alert>
              )}

              {collisions.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Merge blocked: {collisions.length}{" "}
                  {collisions.length === 1 ? "entry" : "entries"} on the source
                  {collisions.length === 1 ? " is" : " are"} for an event and
                  date the target already has. Delete the duplicates first.
                  <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                    {collisions.map((collision) => (
                      <li key={`${collision.sourceEntryId}`}>
                        {collision.event} —{" "}
                        {String(collision.event_date).slice(0, 10)}
                      </li>
                    ))}
                  </Box>
                </Alert>
              )}

              {!mergeBlocked && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {mergePreview.sourceEntryCount} entries will move to{" "}
                  {playerName(mergePreview.target)}, and{" "}
                  {playerName(mergePreview.source)} will be deleted. This cannot
                  be undone.
                </Alert>
              )}
            </Box>
          )}

          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Type the source player's BSA number to confirm"
            value={mergeConfirmText}
            onChange={(event) => setMergeConfirmText(event.target.value)}
            disabled={!mergeReady}
            error={Boolean(mergeConfirmText) && !mergeConfirmed}
            helperText={
              !mergeReady
                ? "Preview the merge first."
                : mergeConfirmText && !mergeConfirmed
                  ? "That does not match the source player's BSA number."
                  : " "
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeOpen(false)} disabled={isMerging}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmMerge}
            disabled={isMerging || !mergeReady || !mergeConfirmed}
          >
            {isMerging ? "Merging..." : "Merge and delete source"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
